import React, { useState, useEffect, useRef, useCallback } from "react";
import wiomLogo from "../assets/svgs/wiom_logo_new.svg";
import heroImg from "../assets/pngs/hero_new.png";
import milkDoorstepImg from "../assets/pngs/milk-doorstep.png";
import newspaperTableImg from "../assets/pngs/newspaper-table.png";
import billPayImg from "../assets/pngs/bill-pay.png";
import { isPincodeServiceable } from "../data/serviceablePincodes";
import "./newHomepageV3.css";

/*
 * V3 uses standard Tailwind/wiom-website sizing: 1rem = 16px.
 * App.js runs a resize() function that sets html.fontSize dynamically
 * (e.g. ~8-9px on most devices), which would break all rem values here.
 * The hook below locks html.fontSize to 16px while this component is
 * mounted and restores the previous behaviour on unmount.
 */
function useBase16px() {
  useEffect(() => {
    const html = document.documentElement;

    // Capture whatever App.js set before we mounted
    const prevFontSize = html.style.fontSize;
    const prevOnResize = window.onresize;

    const lock = () => {
      html.style.fontSize = "16px";
      html.style["-webkit-text-size-adjust"] = "none";
    };

    lock();
    window.onresize = lock; // override App.js resize handler for this page

    return () => {
      // Restore previous state when navigating away from V3
      html.style.fontSize = prevFontSize;
      window.onresize = prevOnResize;
      if (typeof prevOnResize === "function") prevOnResize();
    };
  }, []);
}

// API Configuration
const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://lead-service.i2e1.in"
    : "https://lead-service.qa.i2e1.in";

// Section configuration for GA tracking
const SECTIONS_CONFIG = [
  { id: "hero",         name: "रिचार्ज वाला घर का नेट",                threshold: 0.3 },
  { id: "recognition",  name: "इस महीने बजट थोड़ा टाइट है?",           threshold: 0.3 },
  { id: "expectation",  name: "बुक करने के बाद क्या होता है?",           threshold: 0.3 },
  { id: "how_it_works", name: "कैसे काम करता है?",                       threshold: 0.3 },
  { id: "price",        name: "शुरू ₹23 से",                             threshold: 0.3 },
  { id: "coverage",     name: "आपके शहर में Wiom",                       threshold: 0.3 },
];

// ─── GA / FB helpers (identical to NewHomePageV2) ────────────────────────────

const getUtmParamsObject = () => {
  if (typeof window === "undefined") {
    return { utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "" };
  }
  if (window.__wiomAllUrlParams && typeof window.__wiomAllUrlParams === "object") {
    return { ...window.__wiomAllUrlParams };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source:   params.get("utm_source")   ?? "",
    utm_medium:   params.get("utm_medium")   ?? "",
    utm_campaign: params.get("utm_campaign") ?? "",
    utm_content:  params.get("utm_content")  ?? "",
    utm_term:     params.get("utm_term")     ?? "",
  };
};

const fireScrollEvent = (sectionId, sectionName) => {
  if (typeof window !== "undefined") {
    const eventName = `section_view_${sectionId}_08_04`;
    const payload = { section_name: sectionName, ...getUtmParamsObject() };
    if (window.gtag) window.gtag("event", eventName, payload);
    if (window.fbq)  window.fbq("track", eventName, { content_name: sectionName, content_category: "Section", ...getUtmParamsObject() });
  }
};

const fireLeadEvent = () => {
  if (typeof window !== "undefined") {
    const payload = { lead_source: "website", ...getUtmParamsObject() };
    if (window.gtag) window.gtag("event", "generate_lead", payload);
    if (window.fbq)  window.fbq("track", "Lead", payload);
  }
};

const firePincodeSubmittedEvent = (pincode, serviceable) => {
  if (typeof window !== "undefined") {
    const serviceableStr = serviceable ? "TRUE" : "FALSE";
    const payload = { pincode, serviceable: serviceableStr, ...getUtmParamsObject() };
    if (window.gtag) window.gtag("event", "pincodeSubmitted", payload);
    if (window.fbq)  window.fbq("track", "pincodeSubmitted", payload);
  }
};

const fireAppDownloadedEvent = (pincode, urlParams = {}) => {
  if (typeof window !== "undefined") {
    const params = Object.keys(urlParams).length ? urlParams : getUtmParamsObject();
    const payload = { pincode: pincode || "", ...params };
    if (window.gtag) window.gtag("event", "appDownloaded", payload);
    if (window.fbq)  window.fbq("track", "appDownloaded", payload);
  }
};

const fireUnderstandHowItWorksEvent = (urlParams = {}) => {
  if (typeof window !== "undefined") {
    const params = Object.keys(urlParams).length ? urlParams : getUtmParamsObject();
    const payload = { button_text: "समझें कैसे काम करता है", ...params };
    if (window.gtag) window.gtag("event", "understand_how_it_works", payload);
    if (window.fbq)  window.fbq("track", "understand_how_it_works", payload);
  }
};

const fireGetWiomEvent = (urlParams = {}) => {
  if (typeof window !== "undefined") {
    const params = Object.keys(urlParams).length ? urlParams : getUtmParamsObject();
    const payload = { button_text: "Wiom लें", ...params };
    if (window.gtag) window.gtag("event", "get_wiom", payload);
    if (window.fbq)  window.fbq("track", "get_wiom", payload);
  }
};

const firePincodeEnteredEvent = (pincode) => {
  if (typeof window !== "undefined") {
    const payload = { pincode, ...getUtmParamsObject() };
    if (window.gtag) window.gtag("event", "pincode_entered", payload);
    if (window.fbq)  window.fbq("track", "pincode_entered", payload);
  }
};

const fireDekhenClickedEvent = (pincode) => {
  if (typeof window !== "undefined") {
    const payload = { pincode, ...getUtmParamsObject() };
    if (window.gtag) window.gtag("event", "dekhen_clicked", payload);
    if (window.fbq)  window.fbq("track", "dekhen_clicked", payload);
  }
};

const fireUparPincodeEvent = () => {
  if (typeof window !== "undefined") {
    const payload = { button_text: "ऊपर पिनकोड डालकर देखें", ...getUtmParamsObject() };
    if (window.gtag) window.gtag("event", "upar_pincode_dalkar_dekhen_08_04", payload);
    if (window.fbq)  window.fbq("track", "upar_pincode_dalkar_dekhen_08_04", payload);
  }
};

const firePhoneClickEvent = (location) => {
  if (typeof window !== "undefined") {
    const eventName = location === "header"
      ? "Header_phone_number_clicked_08_04"
      : "Footer_phone_number_clicked_08_04";
    const payload = { ...getUtmParamsObject() };
    if (window.gtag) window.gtag("event", eventName, payload);
    if (window.fbq)  window.fbq("track", eventName, payload);
  }
};

// ─── URL / deep-link helpers (identical to NewHomePageV2) ───────────────────

const getUtmParams = () => {
  if (typeof window !== "undefined") {
    const s = window.location.search;
    return s.startsWith("?") ? s.slice(1) : s;
  }
  return "";
};

const isMobileDevice = () =>
  /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    typeof navigator !== "undefined" ? navigator.userAgent : ""
  );

const getAppDownloadUrl = () => {
  if (!isMobileDevice()) {
    // Branch redirects to wiom.in on desktop — go directly to Play Store instead
    return `https://play.google.com/store/apps/details?id=com.i2e1.wiom_gold${getUtmParams() ? `?${getUtmParams()}` : ""}`;
  }
  const base   = "https://wiom.app.link/aQLtpt7cVCb";
  const params = getUtmParams();
  return params ? `${base}?${params}` : base;
};

// ─── Lead API (identical to NewHomePageV2) ───────────────────────────────────

const submitLead = async (phone, utmParams, pincode) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/lead/campaign`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phone,
        campaign:  "26-Jan-2026",
        utmParams,
        pincode,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, message: data.message || "API request failed", status: data.status, customCode: data.customCode };
    }
    return { success: true, id: data.id };
  } catch (error) {
    console.error("Lead submission error:", error);
    return { success: false, message: "कुछ गलत हो गया। कृपया फिर से कोशिश करें।" };
  }
};

// ─── Gradient text style (shared) ────────────────────────────────────────────
const gradientTextStyle = {
  background: "linear-gradient(135deg, #D9009D 0%, #FF4EC4 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  display: "inline-block",
  paddingTop: "0.2em",
  marginTop: "-0.2em",
};

// ─── Component ───────────────────────────────────────────────────────────────

const NewHomepageV3 = () => {
  // Lock html font-size to 16px (1rem = 16px) for this page
  useBase16px();

  const [pincode,        setPincode]        = useState("");
  const [pincodeError,   setPincodeError]   = useState("");
  const [pincodeStatus,  setPincodeStatus]  = useState(null); // null | 'available' | 'unavailable'
  const [isChecking,     setIsChecking]     = useState(false);
  const [phone,          setPhone]          = useState("");
  const [phoneError,     setPhoneError]     = useState("");
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [submitted,      setSubmitted]      = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sectionRefs     = useRef({});
  const trackedSections = useRef(new Set());
  const revealRefs      = useRef([]);

  const setSectionRef = useCallback((id) => (el) => {
    sectionRefs.current[id] = el;
  }, []);

  const addRevealRef = useCallback((el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  }, []);

  // IntersectionObserver — GA section tracking (same as NewHomePageV2)
  useEffect(() => {
    const observers = [];
    SECTIONS_CONFIG.forEach(({ id, name, threshold }) => {
      const element = sectionRefs.current[id];
      if (!element) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !trackedSections.current.has(id)) {
            trackedSections.current.add(id);
            fireScrollEvent(id, name);
          }
        },
        { threshold }
      );
      observer.observe(element);
      observers.push({ observer, element });
    });
    return () => observers.forEach(({ observer, element }) => observer.unobserve(element));
  }, []);

  // IntersectionObserver — scroll-reveal animations
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    revealRefs.current.forEach((el) => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handlePincodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPincode(val);
    setPincodeError("");
    if (val.length < 6) setPincodeStatus(null);
    if (val.length === 6) firePincodeEnteredEvent(val);
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(val);
    setPhoneError("");
  };

  const handlePincodeCheck = (e) => {
    e.preventDefault();
    if (pincode.length !== 6) { setPincodeError("6 अंकों का पिनकोड डालें"); return; }
    setIsChecking(true);
    setPincodeError("");
    const isServiceable = isPincodeServiceable(pincode);
    setPincodeStatus(isServiceable ? "available" : "unavailable");
    firePincodeSubmittedEvent(pincode, isServiceable);
    setIsChecking(false);
  };

  const handleNotifyMe = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) { setPhoneError("10 अंकों का नंबर डालें"); return; }
    setIsSubmitting(true);
    setPhoneError("");
    const result = await submitLead(phone, getUtmParams(), pincode);
    if (result.success) {
      fireLeadEvent();
      setSubmitted(true);
    } else {
      console.error("API Error:", result.message);
      setPhoneError(result.message || "कुछ गलत हो गया। कृपया फिर से कोशिश करें।");
    }
    setIsSubmitting(false);
  };

  // "Wiom लें →" — fire get_wiom + appDownloaded, then open Branch/fallback link
  const handleGetWiomClick = (e) => {
    e.preventDefault();
    fireGetWiomEvent(getUtmParamsObject());
    fireAppDownloadedEvent(pincode, getUtmParamsObject());
    // On desktop, Branch redirects back to wiom.in — bypass it and go to Play Store directly
    const url = isMobileDevice() && typeof window !== "undefined" && window.__wiomBranchLink
      ? window.__wiomBranchLink
      : getAppDownloadUrl();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Scroll hint — fire understand_how_it_works, scroll to recognition
  const handleScrollHint = () => {
    fireUnderstandHowItWorksEvent(getUtmParamsObject());
    const el = document.getElementById("recognition-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // ─── JSX ──────────────────────────────────────────────────────────────────

  return (
    <main
      style={{
        fontFamily: "'Noto Sans Devanagari', sans-serif",
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        color: "#1A1A1A",
      }}
    >

      {/* ─── HEADER ─────────────────────────────────────────────────── */}
      {/* sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b */}
      <header className="v3-header">
        <div className="v3-container" style={{ height: "4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* h-[29px] sm:h-8 */}
          <img src={wiomLogo} alt="Wiom" className="v3-logo" />
          {/* flex items-center gap-2 text-sm sm:text-base font-medium */}
          <a href="tel:8880322222" className="v3-phone-link" onClick={() => firePhoneClickEvent("header")}>
            <svg
              style={{ width: "1rem", height: "1rem", flexShrink: 0, marginTop: "-2px" }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span style={{ lineHeight: 1 }}>88803 22222</span>
          </a>
        </div>
      </header>

      {/* ─── HERO ────────────────────────────────────────────────────── */}
      {/* relative bg-white overflow-hidden */}
      <section ref={setSectionRef("hero")} style={{ position: "relative", backgroundColor: "#ffffff", overflow: "hidden" }}>
        {/* Subtle right-side accent: absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-wiom-pink/[0.03] */}
        <div style={{
          position: "absolute", top: 0, right: 0,
          width: "50%", height: "100%",
          background: "linear-gradient(to left, rgba(217,0,157,0.03), transparent)",
          pointerEvents: "none",
        }} />

        <div className="v3-container" style={{ position: "relative" }}>
          {/* grid lg:grid-cols-2 gap-8 lg:gap-16 items-center py-12 sm:py-20 lg:py-24 */}
          <div className="v3-hero-grid">

            {/* Left — order-2 lg:order-1 */}
            <div className="v3-hero-left">
              {/* text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-extrabold leading-[1.15] tracking-tight */}
              <h1 className="v3-hero-title">
                रिचार्ज वाला<br />
                <span style={gradientTextStyle}>घर का नेट</span>
              </h1>

              {/* mt-5 text-lg sm:text-xl text-muted leading-relaxed max-w-md */}
              <p className="v3-hero-subtitle">जितने दिन रिचार्ज, उतने दिन नेट।</p>

              {/* PincodeChecker — mt-10 */}
              <div style={{ marginTop: "2.5rem" /* mt-10 */ }}>
                {/* w-full max-w-md */}
                <div style={{ width: "100%", maxWidth: "28rem" }}>

                  {/* mb-3 text-base font-medium */}
                  <p className="v3-pincode-label">पहले अपना एरिया चेक करें</p>

                  {/* ── Idle: pincode input ── */}
                  {pincodeStatus === null && (
                    <form onSubmit={handlePincodeCheck}>
                      {/* flex gap-2 */}
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={pincode}
                          onChange={handlePincodeChange}
                          placeholder="पिनकोड डालें"
                          className="v3-pincode-input"
                          aria-label="पिनकोड"
                        />
                        {/* px-8 h-12 bg-wiom-pink text-white font-semibold rounded-xl disabled:opacity-40 */}
                        <button
                          type="submit"
                          disabled={pincode.length !== 6 || isChecking}
                          className="v3-pincode-btn"
                          style={{ opacity: pincode.length !== 6 ? 0.4 : 1, cursor: pincode.length !== 6 ? "not-allowed" : "pointer" }}
                          onClick={() => fireDekhenClickedEvent(pincode)}
                        >
                          देखें
                        </button>
                      </div>
                      {pincodeError && (
                        <p style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#dc2626" }}>
                          {pincodeError}
                        </p>
                      )}
                      {/* mt-2 text-sm text-muted */}
                      <p className="v3-pincode-hint">देखें आपके एरिया में है या नहीं</p>
                    </form>
                  )}

                  {/* ── Serviceable ── */}
                  {pincodeStatus === "available" && (
                    <div className="v3-fade-in">
                      {/* flex items-center gap-2.5 text-green-700 bg-green-50 px-4 py-3.5 rounded-xl */}
                      <div className="v3-status-chip v3-status-available">
                        <svg style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0 }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                          strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                        <span style={{ fontWeight: 600 }}>आपके एरिया में उपलब्ध है।</span>
                      </div>
                      {/* mt-4 w-full flex items-center justify-center px-6 py-4 ... font-bold rounded-xl */}
                      <a
                        href={getAppDownloadUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="v3-download-btn"
                        onClick={handleGetWiomClick}
                      >
                        <span translate="no">Wiom</span> लें →
                      </a>
                      {/* mt-2 text-center text-sm text-muted */}
                      <p style={{ marginTop: "0.5rem", textAlign: "center", fontSize: "0.875rem", color: "#6B6B6B" }}>
                        App डाउनलोड करके आगे बढ़ें
                      </p>
                    </div>
                  )}

                  {/* ── Not serviceable: notify-me ── */}
                  {pincodeStatus === "unavailable" && !submitted && (
                    <div className="v3-fade-in">
                      {/* flex items-center gap-2.5 text-gray-700 bg-gray-100 px-4 py-3.5 rounded-xl */}
                      <div className="v3-status-chip v3-status-unavailable">
                        <svg style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0, color: "#9CA3AF" }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                        </svg>
                        <span style={{ fontWeight: 500 }}>अभी आपके एरिया में नहीं है।</span>
                      </div>
                      {/* mt-4 bg-surface rounded-xl p-4 border border-gray-200 */}
                      <div className="v3-notify-box">
                        <p style={{ fontSize: "0.875rem", fontWeight: 500, color: "#1A1A1A", margin: "0 0 0.75rem 0" }}>
                          नोटिफ़ाई करें जब आए
                        </p>
                        <form onSubmit={handleNotifyMe} style={{ display: "flex", gap: "0.5rem" }}>
                          {/* flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 */}
                          <input
                            type="tel"
                            inputMode="tel"
                            value={phone}
                            onChange={handlePhoneChange}
                            placeholder="मोबाइल नंबर"
                            maxLength={10}
                            className="v3-phone-input"
                          />
                          {/* px-5 py-3 bg-wiom-pink text-white font-semibold rounded-xl */}
                          <button
                            type="submit"
                            disabled={phone.length === 0 || isSubmitting}
                            className="v3-notify-btn"
                            style={{ opacity: phone.length === 0 ? 0.4 : 1, cursor: phone.length === 0 ? "not-allowed" : "pointer" }}
                          >
                            {isSubmitting ? "..." : "भेजें"}
                          </button>
                        </form>
                        {phoneError && (
                          <p style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#dc2626" }}>
                            {phoneError}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Not serviceable: submitted ── */}
                  {pincodeStatus === "unavailable" && submitted && (
                    <div className="v3-fade-in">
                      <div className="v3-status-chip v3-status-available">
                        <svg style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0 }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                          strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                        <span style={{ fontWeight: 500 }}>
                          धन्यवाद! जैसे ही आपके एरिया में आएगा, हम आपको बताएँगे।
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right — hero image: order-1 lg:order-2 */}
            <div className="v3-hero-right">
              {/* relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 */}
              <div className="v3-hero-image-wrapper">
                <img
                  src={heroImg}
                  alt="Wiom — घर पर परिवार इंटरनेट का आनंद ले रहा है"
                  style={{ width: "100%", height: "auto", objectFit: "cover", aspectRatio: "4/3", display: "block" }}
                  loading="eager"
                />
                {/* absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/10 */}
                <div style={{
                  position: "absolute", left: 0, right: 0, bottom: 0,
                  height: "4rem",
                  background: "linear-gradient(to top, rgba(0,0,0,0.1), transparent)",
                  pointerEvents: "none",
                }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SCROLL HINT ─────────────────────────────────────────────── */}
      {/* bg-white pb-8 text-center */}
      <div style={{ backgroundColor: "#ffffff", paddingBottom: "2rem", textAlign: "center" }}>
        <button
          onClick={handleScrollHint}
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.375rem",
            fontSize: "0.875rem", color: "#6B6B6B",
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "'Noto Sans Devanagari', sans-serif", padding: 0,
          }}
        >
          समझें कैसे काम करता है
          <svg className="v3-bounce" style={{ width: "1rem", height: "1rem" }}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* ─── RECOGNITION ─────────────────────────────────────────────── */}
      {/* relative bg-surface py-20 sm:py-24 lg:py-32 */}
      <section
        id="recognition-section"
        ref={setSectionRef("recognition")}
        className="v3-surface-section"
      >
        {/* dot-pattern overlay */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(circle, #e5e5e5 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          opacity: 0.4,
          pointerEvents: "none",
        }} />

        {/* max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative */}
        <div className="v3-container v3-narrow-3xl" style={{ position: "relative" }}>

          {/* space-y-5 text-lg sm:text-xl lg:text-2xl leading-relaxed */}
          <div ref={addRevealRef} className="v3-reveal">
            <div className="v3-recognition-intro">
              {/* font-semibold text-2xl sm:text-3xl lg:text-4xl */}
              <p className="v3-recognition-lead">इस महीने बजट थोड़ा टाइट है?</p>
              <p style={{ color: "#6B6B6B", margin: 0 }}>मोबाइल डेटा खत्म हो जाता है?</p>
              <p style={{ color: "#6B6B6B", margin: 0 }}>पूरे महीने की ज़रूरत नहीं?</p>
              <p style={{ color: "#6B6B6B", margin: 0 }}>जब ज़रूरत हो तब ही नेट — जब नहीं, तो बंद।</p>
            </div>
          </div>

          {/* mt-14 pt-10 border-t border-gray-200/80 */}
          <div ref={addRevealRef} className="v3-reveal"
            style={{ marginTop: "3.5rem", paddingTop: "2.5rem", borderTop: "1px solid rgba(229,231,235,0.8)" }}>
            <p className="v3-section-heading">
              घर का नेट।{" "}
              <span style={gradientTextStyle}>रिचार्ज से।</span>
            </p>
          </div>

          {/* mt-16 */}
          <div ref={addRevealRef} className="v3-reveal" style={{ marginTop: "4rem" }}>
            {/* grid grid-cols-3 gap-3 sm:gap-5 */}
            <div className="v3-photo-grid">
              <img src={milkDoorstepImg}   alt="दूध का पैकेट दरवाज़े पर"  className="v3-photo-card" loading="lazy" />
              <img src={newspaperTableImg} alt="अखबार मेज़ पर"            className="v3-photo-card" loading="lazy" />
              <img src={billPayImg}         alt="फ़ोन रिचार्ज मोबाइल पर" className="v3-photo-card" loading="lazy" />
            </div>

            {/* mt-10 space-y-3 text-lg sm:text-xl lg:text-2xl */}
            <div className="v3-recognition-texts">
              <p className="v3-recognition-text">दूध रोज़ आता है।</p>
              <p className="v3-recognition-text">अखबार हर दिन आता है।</p>
              <p className="v3-recognition-text">ज़रूरत के हिसाब से खर्चा होता है।</p>
            </div>

            {/* mt-8 text-2xl sm:text-3xl lg:text-4xl font-bold */}
            <p className="v3-section-heading" style={{ marginTop: "2rem" }}>तो घर का नेट क्यों नहीं?</p>
          </div>

        </div>
      </section>

      {/* ─── EXPECTATION ─────────────────────────────────────────────── */}
      {/* bg-white py-20 sm:py-24 lg:py-32 */}
      <section
        ref={setSectionRef("expectation")}
        style={{ backgroundColor: "#ffffff", paddingTop: "5rem", paddingBottom: "5rem" }}
      >
        <div className="v3-container v3-narrow-3xl">
          <div ref={addRevealRef} className="v3-reveal">
            {/* text-2xl sm:text-3xl lg:text-4xl font-bold */}
            <h2 className="v3-section-heading">बुक करने के बाद क्या होता है?</h2>
          </div>

          {/* mt-12 space-y-0 (list items have border-b separation) */}
          <ol style={{ marginTop: "3rem", listStyle: "none", padding: 0, margin: "3rem 0 0 0" }}>
            {[
              { num: "1", text: "हमारा टेक्निशियन 48–72 घंटे में आपके घर आकर नेट लगा देता है।" },
              { num: "2", text: <><span translate="no">Wiom</span> ऐप से रिचार्ज करें, जितने दिन चाहें।</> },
              { num: "3", text: "जितने दिन रिचार्ज, उतने दिन नेट।" },
            ].map((step, i) => (
              <li key={step.num} ref={addRevealRef} className="v3-reveal"
                style={{ transitionDelay: `${i * 100}ms` }}>
                {/* flex items-start gap-5 py-7 border-b border-gray-100 group */}
                <div className="v3-step-item">
                  {/* w-11 h-11 rounded-full gradient text-white font-bold text-lg shadow-md group-hover:scale-110 */}
                  <span className="v3-step-num">{step.num}</span>
                  {/* text-lg sm:text-xl text-foreground pt-2 */}
                  <p className="v3-step-text">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ─── HOW IT WORKS ────────────────────────────────────────────── */}
      {/* relative bg-surface py-20 sm:py-24 lg:py-32 overflow-hidden */}
      <section
        ref={setSectionRef("how_it_works")}
        className="v3-surface-section"
        style={{ overflow: "hidden" }}
      >
        {/* dot-pattern */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(circle, #e5e5e5 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          opacity: 0.3,
          pointerEvents: "none",
        }} />

        {/* max-w-5xl mx-auto */}
        <div className="v3-container v3-narrow-5xl" style={{ position: "relative" }}>
          <div ref={addRevealRef} className="v3-reveal" style={{ textAlign: "center" }}>
            {/* text-2xl sm:text-3xl lg:text-4xl font-bold text-center */}
            <h2 className="v3-section-heading">कैसे काम करता है?</h2>
          </div>

          {/* mt-14 grid sm:grid-cols-3 gap-6 lg:gap-8 */}
          <div className="v3-cards-grid" style={{ marginTop: "3.5rem" }}>
            {[
              {
                num: "1",
                text: "जब ज़रूरत हो — रिचार्ज करो।",
                icon: (
                  <svg style={{ width: "2rem", height: "2rem" }} fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                  </svg>
                ),
              },
              {
                num: "2",
                text: "जितने दिन का रिचार्ज —\nउतने दिन नेट।",
                icon: (
                  <svg style={{ width: "2rem", height: "2rem" }} fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0" />
                  </svg>
                ),
              },
              {
                num: "3",
                text: "रिचार्ज नहीं — नेट बंद।\nकोई झंझट नहीं।",
                icon: (
                  <svg style={{ width: "2rem", height: "2rem" }} fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
            ].map((card, i) => (
              /* bg-white rounded-2xl p-7 lg:p-9 text-center h-full flex flex-col items-center justify-start
                 shadow-sm hover:shadow-xl border transition-all hover:-translate-y-1 group */
              <div key={card.num} ref={addRevealRef} className="v3-reveal v3-card"
                style={{ transitionDelay: `${i * 120}ms` }}>
                {/* w-16 h-16 rounded-2xl bg-gradient … text-wiom-pink mb-6 group-hover:scale-110 */}
                <div className="v3-card-icon">{card.icon}</div>
                {/* text-lg sm:text-xl font-medium leading-relaxed */}
                <p className="v3-card-text" style={{ whiteSpace: "pre-line" }}>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICE ───────────────────────────────────────────────────── */}
      {/* bg-white py-16 sm:py-20 lg:py-24 */}
      <section
        ref={setSectionRef("price")}
        style={{ backgroundColor: "#ffffff", paddingTop: "4rem", paddingBottom: "5rem" }}
      >
        {/* max-w-2xl mx-auto */}
        <div className="v3-container v3-narrow-2xl">
          <div ref={addRevealRef} className="v3-reveal">
            {/* text-lg sm:text-xl text-muted text-center */}
            <p style={{ fontSize: "1.125rem", color: "#6B6B6B", textAlign: "center", margin: 0 }}>
              शुरू{" "}
              {/* price-highlight */}
              <span className="v3-price-highlight">₹23</span>
              {" "}से।
            </p>

            {/* mt-8 flex items-center justify-center gap-6 sm:gap-12 */}
            <div style={{ marginTop: "2rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "1.5rem" }}>
              {/* bg-surface rounded-2xl px-8 py-6 border border-gray-100 */}
              <div className="v3-price-card">
                {/* text-3xl sm:text-4xl font-bold */}
                <p className="v3-price-amount">₹23</p>
                {/* mt-2 text-sm text-muted font-medium */}
                <p className="v3-price-label">एक दिन का</p>
              </div>
              <div style={{ color: "#6B6B6B", fontSize: "1.5rem", fontWeight: 300 }}>/</div>
              <div className="v3-price-card">
                <p className="v3-price-amount">₹565</p>
                <p className="v3-price-label">28 दिन का</p>
              </div>
            </div>

            {/* mt-6 text-base text-muted text-center */}
            <p style={{ marginTop: "1.5rem", fontSize: "1rem", color: "#6B6B6B", textAlign: "center", margin: "1.5rem 0 0 0" }}>
              जितनी ज़रूरत, उतना रिचार्ज।
            </p>
          </div>
        </div>
      </section>

      {/* ─── COVERAGE ────────────────────────────────────────────────── */}
      {/* relative bg-gradient-to-br from-wiom-pink to-wiom-pink-dark py-20 sm:py-24 lg:py-28 overflow-hidden */}
      <section ref={setSectionRef("coverage")} className="v3-coverage-section">
        {/* Abstract background shapes */}
        <div style={{
          position: "absolute", top: 0, left: 0,
          width: "16rem", height: "16rem",
          background: "rgba(255,255,255,0.05)", borderRadius: "50%",
          transform: "translate(-50%, -50%)", pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: 0, right: 0,
          width: "24rem", height: "24rem",
          background: "rgba(255,255,255,0.05)", borderRadius: "50%",
          transform: "translate(33%, 33%)", pointerEvents: "none",
        }} />

        {/* max-w-4xl mx-auto text-center relative */}
        <div className="v3-container" style={{ maxWidth: "56rem", textAlign: "center", position: "relative" }}>
          <div ref={addRevealRef} className="v3-reveal">
            {/* text-2xl sm:text-3xl lg:text-4xl font-bold text-white */}
            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#ffffff", margin: "0 0 1.5rem 0" }}
              className="v3-coverage-heading">
              आपके शहर में <span translate="no">Wiom</span>
            </h2>
            {/* mt-6 text-lg sm:text-xl text-white/90 leading-relaxed */}
            <p style={{ fontSize: "1.125rem", color: "rgba(255,255,255,0.9)", lineHeight: 1.625, margin: "0 0 1.5rem 0" }}
              className="v3-coverage-text">
              Delhi · Noida · Ghaziabad · Meerut · Muzaffarnagar · Lucknow · Agra · Prayagraj · Mumbai · और भी शहरों में आ रहा है।
            </p>
            {/* mt-6 text-base text-white/80 */}
            <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.8)", margin: 0, width: isMobile ? "100vw" : "100%", marginLeft: "-1rem", textAlign: "center"}}>
              आपके एरिया में है या नहीं —{" "}
              <div style={{ display: "inline-block", borderBottom: "1.5px solid rgba(255,255,255,0.8)" }}>
                <button
                  onClick={() => { fireUparPincodeEvent(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  style={{
                    color: "#ffffff", fontWeight: 700,
                    background: "none", border: "none", cursor: "pointer",
                    fontFamily: "'Noto Sans Devanagari', sans-serif", fontSize: "1rem", padding: 0, textDecoration: "underline",
                  }}
                >
                ऊपर पिनकोड डालकर देखें
              </button>
              </div>
            </p>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ──────────────────────────────────────────────────── */}
      {/* bg-foreground text-white py-14 sm:py-16 */}
      <footer style={{ backgroundColor: "#1A1A1A", color: "#ffffff", paddingTop: "3.5rem", paddingBottom: "3.5rem" }}>
        <div className="v3-container">
          {/* flex flex-col sm:flex-row sm:items-start sm:justify-between gap-8 */}
          <div className="v3-footer-inner">

            {/* Left: logo + tagline */}
            <div>
              <div style={{ marginBottom: "-1.4rem" }}>
                <img
                  src={wiomLogo}
                  alt="Wiom"
                  style={{ height: "4rem", width: "auto", filter: "brightness(0) invert(1)" }}
                />
              </div>
              {/* text-white/60 text-base */}
              <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "1rem", margin: 0 }}>
                रिचार्ज वाला घर का नेट
              </p>
            </div>

            {/* Right: phone + address (sm:text-right) */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {/* inline-flex items-center gap-2 text-white font-medium */}
              <a
                href="tel:8880322222"
                onClick={() => firePhoneClickEvent("footer")}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.5rem",
                  color: "#ffffff", textDecoration: "none", fontWeight: 500, fontSize: "1rem",
                  transition: "color 0.2s",
                }}
              >
                <svg style={{ width: "1rem", height: "1rem" }}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                सहायता: 88803 22222
              </a>
              {/* text-white/40 text-sm leading-relaxed */}
              <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.875rem", lineHeight: 1.625, margin: 0 }}>
                Ground Floor, Plot 94, Sector 32,<br />
                Gurugram, Haryana 122001
              </p>
            </div>

          </div>
        </div>
      </footer>

    </main>
  );
};

export default NewHomepageV3;
